import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { not } from 'tio-ui/utilities';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import { formatAsDollars, toLocaleDateString } from '../../../utils/format.ts';
import {
  generateEligibilityBlurb,
  generateLimitsSummary,
  PARTICIPANT_STATE_LABELS,
  LIMIT_LEVEL_LABELS,
  EMPLOYMENT_TYPE_LABELS,
} from '../../../utils/approval-decision-blurb.ts';
import {
  PhShieldCheck,
  PhCheckCircle,
  PhWarningCircle,
  PhXCircle,
  PhCaretDown,
  PhCaretUp,
  PhCurrencyDollar,
  PhUserCheck,
  PhBriefcase,
  PhArrowRight,
} from 'ember-phosphor-icons';
import type IntlService from 'ember-intl/services/intl';
import type {
  LimitsStatusDataSignature,
  LimitsDetailSignature,
} from '../../../services/tuition-assistance.ts';
import type { EligibilityStatus } from 'tio-data/data/resources/eligibility-status.type';

// ApprovalDecisionBanner – decision-assist panel for approvers
// Design principles:
//   1. Sub-second scannability — approvers process many apps per day
//   2. Color signals status before text is read (green/amber/red accents)
//   3. Value-first hierarchy — the answer is prominent, context is secondary
//   4. Progressive disclosure — cascade details on demand

interface ApprovalDecisionBannerSignature {
  Args: {
    eligibilityData?: EligibilityStatus;
    limitsData?: LimitsStatusDataSignature;
    /**
     * The limits read failed, as against succeeding with no limit configured. Without
     * it an absent `limitsData` renders as the affirmative "No limit", which is a
     * claim about the benefit rather than an admission that it was never read.
     */
    limitsUnavailable?: boolean;
    isLoading?: boolean;
    periodsOfEligibility?: Array<[string, string]> | null;
    isPostPaymentReview?: boolean;
    /**
     * Money held by the employee's other applications in this benefit year. The Benefit tile
     * links to the held-funds dialog whenever `listed` holds exist; `total` (cents) and
     * `applications` count only those that hold against this application.
     */
    heldFunds?: { total: number; applications: number; listed: number };
    /** Opens the held-funds dialog; without it the tile shows no link. */
    onOpenHeldFunds?: () => void;
  };
  Element: HTMLDivElement;
}

const participantStateLabel = (state?: string | null) => {
  if (!state) return 'Unknown';
  return PARTICIPANT_STATE_LABELS[state] ?? state.split('.').pop() ?? 'Unknown';
};

const limitLevelLabel = (level: string) => {
  return LIMIT_LEVEL_LABELS[level] ?? level;
};

class ApprovalDecisionBanner extends Component<ApprovalDecisionBannerSignature> {
  @service declare intl: IntlService;

  @tracked showCascade = false;
  @tracked showDetails = false;

  // ── Participant enrollment ──

  get participantState() {
    return this.args.eligibilityData?.participantState;
  }

  get participantLabel() {
    return participantStateLabel(this.participantState);
  }

  get isParticipantOk() {
    const state = this.participantState;
    return state === 'TAS.ParticipantState.ACTIVE' || state === 'TAS.ParticipantState.ELIGIBLE';
  }

  get isParticipantWarning() {
    return this.participantState === 'TAS.ParticipantState.HELD';
  }

  get participantDetail() {
    const data = this.args.eligibilityData;
    if (!data) return '';
    if (data.lockBasis && data.lockDate) {
      const basisLabel = data.lockBasis.replace(/_/g, ' ').toLowerCase();
      return `Locked at ${basisLabel} (${toLocaleDateString(data.lockDate)})`;
    }
    return '';
  }

  // The locked date falls outside every program eligibility period. `null`
  // (check not applicable, e.g. submission-based locks) is NOT a failure.
  get isOutsideEligibilityPeriod() {
    return this.args.eligibilityData?.withinEligibilityPeriod === false;
  }

  // Tile 1 combines participant enrollment with the eligibility-period check:
  // an ACTIVE participant whose locked date is outside the period is NOT ok,
  // so the tile can't read green under an "Ineligible" verdict.
  get isEligibilityOk() {
    return this.isParticipantOk && !this.isOutsideEligibilityPeriod;
  }

  get isEligibilityWarning() {
    return this.isParticipantWarning && !this.isOutsideEligibilityPeriod;
  }

  get eligibilityPeriodDetail() {
    if (!this.isOutsideEligibilityPeriod) return '';
    return 'Locked date is outside the eligibility period';
  }

  // ── Employment ──

  get isDeactivated() {
    return !!this.args.eligibilityData?.deactivatedOn;
  }

  get employmentValue() {
    if (!this.args.eligibilityData) return 'Unknown';
    if (!this.isDeactivated) return 'Active';
    if (this.args.eligibilityData.payDeactivatedEmployees) return 'Deactivated: Payable';
    return 'Inactive';
  }

  get employmentTypeLabel() {
    const raw = this.args.eligibilityData?.employmentType;
    if (!raw) return '';
    return (
      EMPLOYMENT_TYPE_LABELS[raw] ??
      raw
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    );
  }

  get isEmploymentOk() {
    if (!this.args.eligibilityData) return false;
    return !this.isDeactivated;
  }

  get isEmploymentWarning() {
    return this.isDeactivated && !!this.args.eligibilityData?.payDeactivatedEmployees;
  }

  get employmentDetail() {
    const data = this.args.eligibilityData;
    if (!data?.deactivatedOn) return '';
    const date = toLocaleDateString(data.deactivatedOn);
    return data.payDeactivatedEmployees ? `Since ${date} · company pays` : `Since ${date}`;
  }

  // ── Budget ──

  get isCredits() {
    return this.args.limitsData?.limit_capacity === 'TAS.LimitCapacity.CREDITS';
  }

  get budgetAvailable() {
    return this.args.limitsData?.available;
  }

  get benefitLabel() {
    const available = this.budgetAvailable;
    if (this.args.limitsUnavailable) {
      return this.intl.t('common.tas.approval.decision_banner.benefit_unavailable');
    }
    // null means unlimited/no configured limit — treat as a positive state
    if (available === null) return 'Unlimited';
    if (available === undefined) return 'No limit';
    if (this.isCredits) return `${available} credit${available === 1 ? '' : 's'}`;
    return formatAsDollars(available);
  }

  get hasBenefit() {
    const available = this.budgetAvailable;
    // null represents an unlimited/no-limit program — treat as having benefit
    if (available === null) return true;
    // undefined means missing/unknown data
    if (available === undefined) return false;
    return available > 0;
  }

  // Three tones rather than two: a read that failed is not a zero balance, so it must
  // not borrow the confident danger styling that an exhausted benefit earns.
  get benefitTone() {
    if (this.args.limitsUnavailable) return 'unknown';
    if (this.hasBenefit) return 'success';
    return this.isBudgetWarning ? 'warning' : 'danger';
  }

  get benefitTileClass() {
    switch (this.benefitTone) {
      case 'unknown':
        return 'tio-border-default tio-bg-page';
      case 'success':
        return 'tio-border-success tio-bg-success-soft';
      case 'warning':
        return 'tio-border-warning tio-bg-warning-soft';
      default:
        return 'tio-border-danger tio-bg-danger-soft';
    }
  }

  get benefitIconClass() {
    switch (this.benefitTone) {
      case 'unknown':
        return 'tio-fg-disabled';
      case 'success':
        return 'tio-fg-success';
      case 'warning':
        return 'tio-fg-warning';
      default:
        return 'tio-fg-danger';
    }
  }

  get benefitBodyClass() {
    switch (this.benefitTone) {
      case 'unknown':
        return 'tio-fg-muted';
      case 'success':
        return 'tio-fg-success-body';
      case 'warning':
        return 'tio-fg-warning-body';
      default:
        return 'tio-fg-danger-body';
    }
  }

  get benefitHeadingClass() {
    switch (this.benefitTone) {
      case 'unknown':
        return 'tio-fg-muted';
      case 'success':
        return 'tio-fg-success-h';
      case 'warning':
        return 'tio-fg-warning-h';
      default:
        return 'tio-fg-danger-h';
    }
  }

  // When post-payment review and benefit is exhausted, show amber instead of red.
  // If hasBenefit is true (available > 0), green is still accurate — benefit remains.
  get isBudgetWarning() {
    return this.args.isPostPaymentReview && !this.hasBenefit;
  }

  get constrainingLevel() {
    return this.args.limitsData?.soft_limit ?? 'none';
  }

  get constrainingLevelLabel() {
    return limitLevelLabel(this.constrainingLevel);
  }

  get benefitPeriod() {
    const start = this.args.limitsData?.annual_start_date;
    const end = this.args.limitsData?.annual_end_date;
    if (!start || !end) return '';
    return `${toLocaleDateString(start)} – ${toLocaleDateString(end)}`;
  }

  openHeldFunds = () => this.args.onOpenHeldFunds?.();

  get heldFundsLinkLabel() {
    const held = this.args.heldFunds;
    if (!held?.listed || !this.args.onOpenHeldFunds || this.args.limitsUnavailable) {
      return '';
    }
    if (!held.applications) {
      return this.intl.t('common.tas.approval.decision_banner.held_funds_link_read_only');
    }
    return this.intl.t('common.tas.approval.decision_banner.held_funds_link', {
      amount: formatAsDollars(held.total),
      count: held.applications,
    });
  }

  // ── Overall ──

  get hasEligibilityData() {
    return this.args.eligibilityData !== undefined;
  }

  get isEligible() {
    return this.args.eligibilityData?.isEligible ?? false;
  }

  get ignoresPolicies() {
    return this.args.eligibilityData?.ignorePolicies ?? false;
  }

  get accentBorderClass() {
    if (this.ignoresPolicies) return 'tio-approval-decision--warning';
    if (!this.hasEligibilityData) return 'tio-approval-decision--unknown';
    if (this.isEligible) return 'tio-approval-decision--success';
    return 'tio-approval-decision--danger';
  }

  // ── Cascade ──

  get cascadeLevels() {
    const details = this.args.limitsData?.details;
    if (!details) return [];

    const softLimit = this.args.limitsData?.soft_limit ?? 'none';
    const isCredits = this.isCredits;

    const fmt = (cents: number) => {
      if (isCredits) return `${cents} cr`;
      return formatAsDollars(cents);
    };

    const order: Array<{ key: keyof typeof details; label: string }> = [
      { key: 'scheme', label: 'Program' },
      { key: 'batch', label: 'Batch' },
      { key: 'product', label: 'Product' },
      { key: 'company', label: 'Company' },
    ];

    return order.map(({ key, label }) => {
      const detail: LimitsDetailSignature | undefined = details[key];
      const isConstraining = softLimit === key || softLimit === `${key}_annual`;
      const limit = detail?.annual_limit;
      const available = detail?.available;
      return {
        key,
        label,
        hasLimit: limit != null,
        limitFormatted: limit != null ? fmt(limit) : null,
        usageFormatted: fmt(detail?.annual_usage ?? 0),
        hasAvailable: available != null,
        availableFormatted: available != null ? fmt(available) : null,
        isConstraining,
      };
    });
  }

  // ── Blurb ──

  get eligibilityBlurb() {
    const data = this.args.eligibilityData;
    if (!data) return '';
    return generateEligibilityBlurb(data);
  }

  get limitsSummary() {
    const data = this.args.limitsData;
    if (!data) return '';
    return generateLimitsSummary(data);
  }

  get detailBlurb() {
    const parts = [this.eligibilityBlurb, this.limitsSummary].filter(Boolean);
    return parts.join(' ');
  }

  get formattedPeriods() {
    const periods = this.args.periodsOfEligibility;
    if (!periods || periods.length === 0) return [];
    return periods.map(([start, end]: [string, string]) => ({
      range: `${toLocaleDateString(start)} – ${toLocaleDateString(end)}`,
    }));
  }

  toggleCascade = () => {
    this.showCascade = !this.showCascade;
  };

  toggleDetails = () => {
    this.showDetails = !this.showDetails;
  };

  <template>
    <div
      data-test-approval-decision-banner
      class="tio-approval-decision rounded-lg border shadow-sm border-l-4 overflow-hidden
        {{this.accentBorderClass}}"
      ...attributes
    >
      <div class="tio-row-between px-5 py-3 tio-bg-page border-b tio-border-subtle">
        <div class="tio-row-tight">
          <PhShieldCheck @size="18" class="app-status-text-brand" />
          <span class="tio-text-sm-semibold tio-fg-primary tracking-tight">
            Approval Decision Assist
          </span>
        </div>

        {{#if this.ignoresPolicies}}
          <Chip @intent="warning" @size="sm" @appearance="faded">Admin Override</Chip>
        {{else if (not this.hasEligibilityData)}}
          <div class="tio-row-snug">
            <PhWarningCircle @size="16" class="tio-fg-disabled" />
            <span class="tio-text-sm-semibold tio-fg-muted">Unknown</span>
          </div>
        {{else if this.isEligible}}
          <div class="tio-row-snug">
            <PhCheckCircle @size="16" class="tio-fg-success" />
            <span class="tio-text-sm-semibold tio-fg-success-body">Eligible</span>
          </div>
        {{else}}
          <div class="tio-row-snug">
            <PhXCircle @size="16" class="tio-fg-danger" />
            <span class="tio-text-sm-semibold tio-fg-danger-body">Ineligible</span>
          </div>
        {{/if}}
      </div>

      <div class="px-5 py-4">
        {{#if @isLoading}}
          <div class="grid grid-cols-3 gap-3">
            <div class="animate-pulse rounded-lg tio-bg-sunken h-20"></div>
            <div class="animate-pulse rounded-lg tio-bg-sunken h-20 delay-75"></div>
            <div class="animate-pulse rounded-lg tio-bg-sunken h-20 delay-150"></div>
          </div>
        {{else}}
          <div class="grid grid-cols-3 gap-3">

            <div
              class="rounded-lg border px-3.5 py-3
                {{if
                  this.isEligibilityOk
                  'tio-border-success tio-bg-success-soft'
                  (if
                    this.isEligibilityWarning
                    'tio-border-warning tio-bg-warning-soft'
                    'tio-border-danger tio-bg-danger-soft'
                  )
                }}"
            >
              <div class="tio-row-snug mb-2">
                <PhUserCheck
                  @size="14"
                  class="{{if
                      this.isEligibilityOk
                      'tio-fg-success'
                      (if this.isEligibilityWarning 'tio-fg-warning' 'tio-fg-danger')
                    }}"
                />
                <span
                  class="tio-text-xs-medium uppercase tracking-wider
                    {{if
                      this.isEligibilityOk
                      'tio-fg-success-body'
                      (if this.isEligibilityWarning 'tio-fg-warning-body' 'tio-fg-danger-body')
                    }}"
                >
                  Eligibility
                </span>
              </div>
              <div
                class="tio-text-lg-bold leading-tight
                  {{if
                    this.isEligibilityOk
                    'tio-fg-success-h'
                    (if this.isEligibilityWarning 'tio-fg-warning-h' 'tio-fg-danger-h')
                  }}"
              >
                {{this.participantLabel}}
              </div>
              {{#if this.participantDetail}}
                <div
                  class="text-xs mt-1 leading-tight
                    {{if
                      this.isEligibilityOk
                      'tio-fg-success-body'
                      (if this.isEligibilityWarning 'tio-fg-warning-body' 'tio-fg-danger-body')
                    }}"
                >
                  {{this.participantDetail}}
                </div>
              {{/if}}
              {{#if this.eligibilityPeriodDetail}}
                <div class="tio-text-xs-medium mt-1 leading-tight tio-fg-danger-body">
                  {{this.eligibilityPeriodDetail}}
                </div>
              {{/if}}
            </div>

            <div
              class="rounded-lg border px-3.5 py-3
                {{if
                  this.isEmploymentOk
                  'tio-border-success tio-bg-success-soft'
                  (if
                    this.isEmploymentWarning
                    'tio-border-warning tio-bg-warning-soft'
                    'tio-border-danger tio-bg-danger-soft'
                  )
                }}"
            >
              <div class="tio-row-snug mb-2">
                <PhBriefcase
                  @size="14"
                  class="{{if
                      this.isEmploymentOk
                      'tio-fg-success'
                      (if this.isEmploymentWarning 'tio-fg-warning' 'tio-fg-danger')
                    }}"
                />
                <span
                  class="tio-text-xs-medium uppercase tracking-wider
                    {{if
                      this.isEmploymentOk
                      'tio-fg-success-body'
                      (if this.isEmploymentWarning 'tio-fg-warning-body' 'tio-fg-danger-body')
                    }}"
                >
                  Employment
                </span>
              </div>
              <div
                class="tio-text-lg-bold leading-tight
                  {{if
                    this.isEmploymentOk
                    'tio-fg-success-h'
                    (if this.isEmploymentWarning 'tio-fg-warning-h' 'tio-fg-danger-h')
                  }}"
              >
                {{this.employmentValue}}
              </div>
              {{#if this.employmentTypeLabel}}
                <div
                  class="text-xs mt-1 leading-tight
                    {{if
                      this.isEmploymentOk
                      'tio-fg-success-body'
                      (if this.isEmploymentWarning 'tio-fg-warning-body' 'tio-fg-danger-body')
                    }}"
                >
                  {{this.employmentTypeLabel}}
                </div>
              {{/if}}
              {{#if this.employmentDetail}}
                <div
                  class="text-xs mt-1 leading-tight
                    {{if
                      this.isEmploymentOk
                      'tio-fg-success-body'
                      (if this.isEmploymentWarning 'tio-fg-warning-body' 'tio-fg-danger-body')
                    }}"
                >
                  {{this.employmentDetail}}
                </div>
              {{/if}}
            </div>

            <div
              class="rounded-lg border px-3.5 py-3 {{this.benefitTileClass}}"
              data-test-approval-decision-benefit-tile
            >
              <div class="tio-row-snug mb-2">
                {{#if @limitsUnavailable}}
                  <PhWarningCircle @size="14" class={{this.benefitIconClass}} />
                {{else}}
                  <PhCurrencyDollar @size="14" class={{this.benefitIconClass}} />
                {{/if}}
                <span class="tio-text-xs-medium uppercase tracking-wider {{this.benefitBodyClass}}">
                  Benefit
                </span>
              </div>
              <div class="tio-text-lg-bold leading-tight {{this.benefitHeadingClass}}">
                {{this.benefitLabel}}
              </div>
              {{#if @limitsUnavailable}}
                <div class="tio-text-xs-normal mt-1 leading-tight {{this.benefitBodyClass}}">
                  {{t "common.tas.approval.decision_banner.benefit_unavailable_detail"}}
                </div>
              {{else if this.isBudgetWarning}}
                <div class="text-xs tio-fg-warning-body mt-1 leading-tight">
                  {{t "common.tas.approval.post_payment_review.already_paid"}}
                </div>
              {{else}}
                {{#unless (eq this.constrainingLevel "none")}}
                  <div class="text-xs mt-1 leading-tight {{this.benefitBodyClass}}">
                    {{this.constrainingLevelLabel}}
                  </div>
                {{/unless}}
              {{/if}}
              {{#if this.benefitPeriod}}
                <div class="text-xs mt-1 leading-tight whitespace-nowrap {{this.benefitBodyClass}}">
                  {{this.benefitPeriod}}
                </div>
              {{/if}}
              {{#if this.heldFundsLinkLabel}}
                <Button
                  @intent="primary"
                  @appearance="link"
                  @size="sm"
                  @icon={{PhArrowRight}}
                  @iconPlacement="end"
                  @testHook="approval-decision__held-funds"
                  class="mt-1 text-left underline underline-offset-2"
                  data-test-approval-decision-held-funds-link
                  {{on "click" this.openHeldFunds}}
                >
                  {{this.heldFundsLinkLabel}}
                </Button>
              {{/if}}
            </div>
          </div>

          {{#if this.formattedPeriods.length}}
            <div
              class="mt-3 rounded-lg border tio-border-subtle tio-bg-page px-3.5 py-3"
              data-test-top-level-eligibility-periods
            >
              <p class="tio-text-xs-medium tio-fg-muted mb-1">Program eligibility periods</p>
              {{#each this.formattedPeriods as |period|}}
                <p class="text-xs tio-fg-secondary" data-test-eligibility-period>
                  {{period.range}}
                </p>
              {{/each}}
            </div>
          {{/if}}

          {{#if this.detailBlurb}}
            <div class="mt-3 pt-3 border-t tio-border-subtle">
              <button
                type="button"
                class="tio-approval-decision__disclosure tio-row-snug tio-text-xs-medium transition-colors group"
                data-test-details-toggle
                aria-expanded={{if this.showDetails "true" "false"}}
                aria-controls="approval-details-content"
                {{on "click" this.toggleDetails}}
              >
                {{#if this.showDetails}}
                  <PhCaretUp @size="12" class="transition-transform" />
                {{else}}
                  <PhCaretDown @size="12" class="transition-transform" />
                {{/if}}
                <span class="group-hover:underline">View details</span>
              </button>

              {{#if this.showDetails}}
                <div id="approval-details-content">
                  {{#if this.detailBlurb}}
                    <p class="mt-2 text-xs tio-fg-muted leading-relaxed" data-test-detail-blurb>
                      {{this.detailBlurb}}
                    </p>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{#if this.cascadeLevels.length}}
            <div class="mt-3 pt-3 border-t tio-border-subtle">
              <button
                type="button"
                class="tio-approval-decision__disclosure tio-row-snug tio-text-xs-medium transition-colors group"
                data-test-cascade-toggle
                aria-expanded={{if this.showCascade "true" "false"}}
                aria-controls="approval-cascade-content"
                {{on "click" this.toggleCascade}}
              >
                {{#if this.showCascade}}
                  <PhCaretUp @size="12" class="transition-transform" />
                {{else}}
                  <PhCaretDown @size="12" class="transition-transform" />
                {{/if}}
                <span class="group-hover:underline">Limits Cascade</span>
              </button>

              {{#if this.showCascade}}
                <div
                  class="mt-2.5 rounded-lg border tio-border-subtle overflow-hidden"
                  id="approval-cascade-content"
                  data-test-limits-cascade
                >
                  {{#each this.cascadeLevels as |level|}}
                    <div
                      class="tio-row-between py-2 px-3 text-sm
                        {{if
                          level.isConstraining
                          'tio-approval-cascade__level--constraining tio-bg-warning-soft border-l-2'
                          'tio-approval-cascade__level--regular tio-bg-surface border-l-2'
                        }}
                        tio-approval-cascade__level"
                      data-test-cascade-level
                    >
                      <div class="tio-row-tight">
                        {{#if level.isConstraining}}
                          <PhWarningCircle @size="14" class="tio-fg-warning shrink-0" />
                        {{/if}}
                        <span
                          class="font-medium
                            {{if level.isConstraining 'tio-fg-warning-h' 'tio-fg-secondary'}}"
                        >
                          {{level.label}}
                        </span>
                      </div>
                      <div class="tio-row-loose text-xs tabular-nums">
                        {{#if level.hasLimit}}
                          <div class="tio-fg-secondary">
                            <span class="font-medium tio-fg-muted">Limit</span>
                            {{level.limitFormatted}}
                          </div>
                        {{/if}}
                        <div class="tio-fg-secondary">
                          <span class="font-medium tio-fg-muted">Used</span>
                          {{level.usageFormatted}}
                        </div>
                        {{#if level.hasAvailable}}
                          <div
                            class="{{if
                                level.isConstraining
                                'tio-fg-warning-body font-semibold'
                                'tio-fg-muted'
                              }}"
                          >
                            <span class="font-medium">Avail</span>
                            {{level.availableFormatted}}
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{/if}}
            </div>
          {{/if}}
        {{/if}}
      </div>
    </div>
  </template>
}

export default ApprovalDecisionBanner;

export type { ApprovalDecisionBannerSignature };
